import { useCallback, useEffect, useState } from 'react';
import * as React from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  FormControl, InputLabel, MenuItem, Select, IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ReplayIcon from '@mui/icons-material/Replay';
import { ensureSafariAudioSession } from '../../../utils/platform';

const useStyles = makeStyles()(() => ({
  select: {
    width: '100%',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },
}));

interface InputDeviceSelectorProps {
  onDeviceSelect: (deviceId: string) => void;
  disabled: boolean;
}

const INPUT_DEVICE_STORAGE_KEY = 'inputDevice';

const InputDeviceSelector: React.FC<InputDeviceSelectorProps> = ({ onDeviceSelect, disabled }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [inputDevice, setInputDevice] = useState('');
  const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [error, setError] = useState(false);

  const fetchDevices = useCallback(() => {
    ensureSafariAudioSession();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const inputs = devices.filter((device) => device.kind === 'audioinput');

          const storedDevice = localStorage.getItem(INPUT_DEVICE_STORAGE_KEY);
          setInputDevices(inputs);
          if (inputs.length > 0) {
            if (storedDevice && inputs.find((input) => input.deviceId === storedDevice)) {
              // If the stored device is still available, use it
              setInputDevice(storedDevice);
              onDeviceSelect(storedDevice);
            } else {
              // Otherwise, use the first device (default device)
              setInputDevice(inputs[0].deviceId);
              onDeviceSelect(inputs[0].deviceId);
            }
          }
          // Stop the stream
          stream.getTracks().forEach((track) => track.stop());
          setError(false);
        });
      })
      .catch((e) => {
        setError(true);
        console.error('No audio devices found', e);
      });
  }, [onDeviceSelect]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const deviceId = event.target.value;
    setInputDevice(deviceId);
    onDeviceSelect(deviceId);
  };

  useEffect(() => {
    if (inputDevice) {
      localStorage.setItem(INPUT_DEVICE_STORAGE_KEY, inputDevice);
    }
  }, [inputDevice]);

  return (
    <div>
      {error ? (
        <div className={classes.errorContainer}>
          <span>{t('song_recorder.no_audio_devices')}</span>
          <IconButton id="btn_recording_no_device_repeat" onClick={fetchDevices}>
            <ReplayIcon />
          </IconButton>
        </div>
      ) : (
        <FormControl variant="standard" className={classes.select} disabled={disabled}>
          <InputLabel id="input-device-select-label">{t('song_recorder.input_device')}</InputLabel>
          <Select
            labelId="input-device-select-label"
            id="input-device-select"
            value={inputDevice}
            onChange={handleChange}
            disabled={disabled}
            size="small"
          >
            {inputDevices.map((device, i) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.label === '' ? device.deviceId : device.label}
                {i === 0 ? `(${t('song_recorder.default')})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
};

export default InputDeviceSelector;
